import React, { useState, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';

const TARGETS = [2, 3, 4, 5, 7];
const ROUNDS = 5;

function TimingGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [phase, setPhase] = useState('idle'); // idle | ready | timing | feedback | done
  const [round, setRound] = useState(1);
  const [target, setTarget] = useState(null);
  const [results, setResults] = useState([]);
  const [diff, setDiff] = useState(null);
  const [best, setBest] = useStorage('gm_timinggame_best', 0);
  const startRef = useRef(null);

  const nextTarget = () => TARGETS[Math.floor(Math.random() * TARGETS.length)];

  const startRound = (r) => {
    setRound(r);
    setDiff(null);
    setTarget(nextTarget());
    setPhase('ready');
  };

  const handleStart = () => {
    startRef.current = Date.now();
    setPhase('timing');
  };

  const handleStop = () => {
    const elapsed = Date.now() - startRef.current;
    const targetMs = target * 1000;
    const penalty = Math.min(100, Math.abs(elapsed - targetMs) / targetMs * 100);
    const accuracy = Math.round(100 - penalty);
    const diffSec = ((elapsed - targetMs) / 1000).toFixed(1);
    const newResults = [...results, accuracy];
    setDiff(parseFloat(diffSec));
    setResults(newResults);
    if (newResults.length >= ROUNDS) {
      const avg = Math.round(newResults.reduce((a, b) => a + b, 0) / ROUNDS);
      saveGameScore('timinggame', avg);
      if (avg > best) setBest(avg);
      setPhase('done');
    } else {
      setPhase('feedback');
    }
  };

  const avgScore = results.length
    ? Math.round(results.reduce((a, b) => a + b, 0) / results.length)
    : 0;

  const reset = () => { setResults([]); setDiff(null); startRound(1); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
      </div>

      {phase === 'idle' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⏱</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Tijdrechter' : 'Time Judge'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.7 }}>
            {nl ? 'Een doeltijd verschijnt. Tik Start en Stop wanneer je denkt dat de tijd om is. Geen aftelling zichtbaar!' : 'A target time appears. Tap Start, count mentally, then tap Stop. No countdown shown!'}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {best}%</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={() => { setResults([]); startRound(1); }}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(phase === 'ready' || phase === 'timing') && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 4 }}>{nl ? 'Ronde' : 'Round'} {round} / {ROUNDS}</div>
          <div style={{ color: _T.accent, fontSize: 36, fontWeight: 700, marginBottom: 6 }}>{target}s</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 20 }}>
            {nl ? 'Stop na precies deze tijd' : 'Stop after exactly this time'}
          </div>
          {phase === 'ready' && (
            <button style={s_PB()} onClick={handleStart}>{nl ? 'Start tellen' : 'Start counting'}</button>
          )}
          {phase === 'timing' && (
            <button style={s_PB({ background: `linear-gradient(135deg,#C05050,#E07070)` })} onClick={handleStop}>{nl ? 'Stop!' : 'Stop!'}</button>
          )}
        </div>
      )}

      {phase === 'feedback' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 8 }}>{nl ? 'Ronde' : 'Round'} {round} / {ROUNDS}</div>
          <div style={{ fontSize: 38, marginBottom: 4 }}>{Math.abs(diff) <= 0.3 ? '🎯' : Math.abs(diff) <= 1 ? '👍' : '😬'}</div>
          <div style={{ color: diff > 0 ? C.orange : C.teal, fontSize: 20, fontWeight: 700 }}>
            {diff > 0 ? `+${diff}s` : `${diff}s`}
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
            {diff > 0 ? (nl ? 'te laat' : 'too late') : (nl ? 'te vroeg' : 'too early')}
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
            {nl ? 'Nauwkeurigheid' : 'Accuracy'}: <span style={{ color: C.white, fontWeight: 700 }}>{results[results.length - 1]}%</span>
          </div>
          <button style={s_PB()} onClick={() => startRound(round + 1)}>{nl ? 'Volgende ronde' : 'Next round'}</button>
        </div>
      )}

      {phase === 'done' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⏱</div>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700 }}>{avgScore}%</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'gemiddelde nauwkeurigheid' : 'average accuracy'}</div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 16 }}>
            {results.map((r, i) => <span key={i} style={{ marginRight: 8 }}>R{i + 1}: {r}%</span>)}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, marginBottom: 12 }}>🏆 Best: {best}%</div>}
          <button style={s_PB()} onClick={reset}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default TimingGame;
